import React, { useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm} from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import CountryService from "../../services/countryService";
import CandidateService from "../../services/candidateService";
import { useAxios } from "../../contexts/axiosContext";
import { PERSONAL_DOCUMENT_OPTIONS } from "../../config/constants";
import { formatBirthdateForForm } from "../../utils/formatters";

import InputField from "../input/InputField";
import SelectField from "../input/SelectField";
import TextAreaField from "../input/TextAreaField";
import FormButton from "../../components/button/FormButton";
import DynamicListSection from "./DynamicListSection";
import DocumentUpload from "../document/DocumentUpload";


const Form = ({ initialData, isEditing, onDocumentSelect, activeDocId, onSaveSuccess, onSaveError, onCancel}) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { authAxios } = useAxios();
    const candidateService = useMemo(() => CandidateService(), []);
    const countryService = useMemo(() => CountryService(), []);

    const generateId = () => Date.now() + Math.random();
    const isEnglish = i18n.language.startsWith('en');

    const [educationList, setEducationList] = useState([{ id: generateId(), level: '', title: '' }]);
    const [linksList, setLinksList] = useState([{ id: generateId(), type: '', url: '' }]);
    const [documentsList, setDocumentsList] = useState([]);

    const [countryOptions, setCountryOptions] = useState([]);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isValid }
    } = useForm({
        mode: 'onChange',
    });

    const resolveCountryValue = (rawCountry) => {
      if (!rawCountry) return "";

      const trimmed = String(rawCountry).trim();
      if (/^[A-Z]{2}$/.test(trimmed)) return trimmed;

      const lower = trimmed.toLowerCase();
      const match = countryOptions.find((opt) => String(opt.label).toLowerCase() === lower);
      return match?.value ?? "";
    };

    useEffect(() => {
      const controller = new AbortController();

      const loadCountries = async () => {
        try {
          const data = await countryService.list({ signal: controller.signal });

          const lang = (i18n.language || "es").toLowerCase();
          const isEn = lang.startsWith("en");

          const options = (Array.isArray(data) ? data : [])
            .map((c) => ({
              value: c.iso2,
              label: isEn ? c.nameEN : c.nameES,
            }))
            .sort((a, b) => a.label.localeCompare(b.label, lang));

          setCountryOptions(options);
        } catch (err) {
          if (err.name === 'CanceledError') return;
          setCountryOptions([]);
        }
      };

      loadCountries();
      return () => controller.abort();
    }, [countryService, i18n.language]);

    useEffect(() => {
      if (!initialData) return;

        const phonePrefix =
          initialData.phone?.phonePrefix ??
          initialData.phonePrefix ??
          "";

        const phoneNumber =
          initialData.phone?.phoneNumber ??
          initialData.phoneNumber ??
          "";

        const city =
          initialData.location?.city ??
          initialData.city ??
          "";

        const rawCountry =
          initialData.location?.country ??
          initialData.country ??
          "";

        const comments =
          initialData.comments ?? "";

        const personalId =
          initialData.personalDocument?.personalId ??
          initialData.personalId ??
          "";

        const personalType =
          initialData.personalDocument?.type ??
          initialData.typePersonalDocument ??
          "";

        const mappedData = {
          name: initialData.firstName || "",
          surname: initialData.lastName || "",
          email: initialData.email || "",

          phonePrefix: phonePrefix?.toString?.() ?? "",
          phoneNumber: phoneNumber?.toString?.() ?? "",

          job: initialData.currentPosition || "",

          city: city || "",
          country: resolveCountryValue(rawCountry),

          comments: comments ?? "",

          personalId: personalId ?? "",
          typePersonalDocument: personalType ?? "",

          birthdate: formatBirthdateForForm(initialData.birthdate, isEnglish),
        };

      reset(mappedData);

      const edu = initialData.degrees ?? initialData.education ?? [];
      if (Array.isArray(edu) && edu.length > 0) {
        setEducationList(
          edu.map((item) => ({
            id: generateId(),
            level: item?.level ?? "",
            title: item?.title ?? "",
          }))
        );
      } else {
        setEducationList([{ id: generateId(), level: "", title: "" }]);
      }

      if (Array.isArray(initialData.linksToProfile) && initialData.linksToProfile.length > 0) {
        setLinksList(
          initialData.linksToProfile.map((link) => ({
            id: generateId(),
            type: link?.type ?? "",
            url: link?.url ?? "",
          }))
        );
      } else {
        setLinksList([{ id: generateId(), type: "", url: "" }]);
      }

      if (Array.isArray(initialData.candidateDocuments)) {
        setDocumentsList(initialData.candidateDocuments);
      } else {
        setDocumentsList([]);
      }
    }, [initialData, reset, isEnglish, countryOptions]);

    const toPayload = (data) => {
      const phonePrefix = data.phonePrefix ? Number(String(data.phonePrefix).replace(/\D/g, "")) : null;
      const phoneNumber = data.phoneNumber ? Number(String(data.phoneNumber).replace(/\D/g, "")) : null;

      return {
        firstName: data.name,
        lastName: data.surname || null,
        comments: data.comments ?? null,
        email: data.email,
        birthdate: data.birthdate || null,
        phone: { phonePrefix, phoneNumber },
        currentPosition: data.job || null,
        location: {
          city: data.city || null,
          country: data.country || null,
        },
        degrees: (educationList ?? []).map((e) => ({
          level: e.level || null,
          title: e.title || null,
        })),
        linksToProfile: (linksList ?? []).map((l) => ({
          type: l.type || null,
          url: l.url || null,
        })),
        candidateDocuments: (documentsList ?? []).filter((d) => !d?.file),
        personalDocument: {
          personalId: data.personalId || null,
          type: data.typePersonalDocument || null,
        },
      };
    };

    const onSubmit = async (data) => {
        try {
          const formattedData = { ...data };
          if (data.birthdate) {
            const [p1, p2, year] = data.birthdate.split("/");
            const day = isEnglish ? p2 : p1;
            const month = isEnglish ? p1 : p2;
            formattedData.birthdate = `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
          }

          const params = toPayload(formattedData);

          const files = Array.isArray(documentsList)
             ? documentsList
               .map((d) => (d instanceof File ? d : d?.file))
               .filter(Boolean)
             : [];

          if (isEditing) {
            await candidateService.update(initialData.id, params, files);
            onSaveSuccess?.();
            return;
          }

          await candidateService.create(params, files);
          if (onSaveSuccess) {
            onSaveSuccess();
            return;
          }
          navigate("/candidates");
        } catch (e) {
          onSaveError?.(e);
        }
    };

    const updateList = (index, field, value, list, setList) => {
        const newList = [...list];
        newList[index][field] = value;
        setList(newList);
    };

    const removeItem = (index, list, setList) => {
        const newList = [...list];
        newList.splice(index, 1);
        setList(newList);
    };

    const addItem = (template, list, setList) => {
       setList([...list, { ...template, id: generateId() }]);
    };


  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="w-full">

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-darkGray mb-6">
            {t('FORM.CONTACT.TITLE')}
        </h2>
        <div className="space-y-5">
          <InputField
            label={`${t('FORM.CONTACT.NAME')} *`}
            type="text"
            placeholder={t('FORM.CONTACT.PLACEHOLDERS.NAME')}
            {...register("name", {
                required: t('FORM.VALIDATION.REQUIRED')
              })}
              error={errors.name}
            />

          <InputField
            label={t('FORM.CONTACT.SURNAME')}
            type="text"
            placeholder={t('FORM.CONTACT.PLACEHOLDERS.SURNAME')}
            {...register("surname")}
          />

          <InputField
            label={t('FORM.CONTACT.EMAIL')}
            type="email"
            placeholder={t('FORM.CONTACT.PLACEHOLDERS.EMAIL')}
            {...register("email", {
              required: t('FORM.VALIDATION.REQUIRED'),
              pattern: {
                value: /^[A-Za-z0-9._+-]+@[A-Za-z0-9-]+\.[A-Za-z]{2,}$/,
                message: t('FORM.VALIDATION.INVALID_EMAIL')
              }
            })}
            error={errors.email}
          />

          <SelectField
            label="Documento de identificación"
            placeholder="- Escoge -"
            options={PERSONAL_DOCUMENT_OPTIONS}
            {...register("typePersonalDocument")}
          />

          <InputField
            label="Número de documento"
            type="text"
            placeholder="Introduce el número"
            {...register("personalId")}
          />

          <div className="flex gap-4 items-start">
            <InputField
              label={t('FORM.CONTACT.PREFIX')}
              type="text"
              placeholder="+34"
              className="w-1/4"
              {...register("phonePrefix")}
              error={errors.phonePrefix}
            />

            <InputField
              label={t('FORM.CONTACT.PHONE')}
              type="tel"
              placeholder="612 34 56 78"
              className="flex-1"
              {...register("phoneNumber", {
                pattern: {
                  value: /^[0-9]+$/,
                  message: t('FORM.VALIDATION.NUMBERS_ONLY')
                }
              })}
              error={errors.phoneNumber}
            />
          </div>
          <InputField
            label={t('FORM.CONTACT.BIRTHDATE')}
            type="text"
            placeholder={t('FORM.CONTACT.DATE')}
            inputMode="numeric"
            onInput={(e) => {
              let value = e.target.value.replace(/\D/g, '');

              if (value.length > 2) {
                value = value.slice(0, 2) + '/' + value.slice(2);
              }
              if (value.length > 5) {
                value = value.slice(0, 5) + '/' + value.slice(5);
              }

              e.target.value = value.slice(0, 10);
            }}
            {...register("birthdate", {
              validate: value => {
                if (!value) return true;

                const regex = /^\d{2}\/\d{2}\/\d{4}$/;
                if (!regex.test(value)) {
                  return t('FORM.VALIDATION.INVALID_DATE');
                }

                const parts = value.split('/').map(Number);
                let day, month, year;
                if (isEnglish) {
                    [month, day, year] = parts;
                } else {
                    [day, month, year] = parts;
                }

                const date = new Date(year, month - 1, day);

                const isReal =
                  date.getFullYear() === year &&
                  date.getMonth() === month - 1 &&
                  date.getDate() === day;

                if (!isReal) {
                  return t('FORM.VALIDATION.INVALID_DATE');
                }

                const today = new Date();
                today.setHours(0, 0, 0, 0);

                if (date >= today) {
                  return t('FORM.VALIDATION.BIRTHDATE_PAST');
                }

                return true;
              }
            })}
            error={errors.birthdate}
          />
          <InputField
            label={t('FORM.CONTACT.JOB')}
            type="text"
            placeholder={t('FORM.CONTACT.PLACEHOLDERS.JOB')}
            {...register("job")}
          />
          <InputField
            label={t('FORM.CONTACT.CITY')}
            type="text"
            placeholder={t('FORM.CONTACT.PLACEHOLDERS.CITY')}
            {...register("city")}
           />
          <SelectField
            label={t('FORM.CONTACT.COUNTRY')}
            placeholder={t('FORM.CONTACT.PLACEHOLDERS.COUNTRY')}
            options={countryOptions}
            {...register("country")}
          />
        </div>
      </section>

      <DynamicListSection
        title={t('FORM.EDUCATION.TITLE')}
        items={educationList}
        addButtonLabel={t('FORM.EDUCATION.ADD_BUTTON')}

        onAdd={() => addItem({ level: '', title: '' }, educationList, setEducationList)}
        onRemove={(index) => removeItem(index, educationList, setEducationList)}
        onUpdate={(index, field, value) => updateList(index, field, value, educationList, setEducationList)}

        fields={[
            {
                key: 'level',
                label: t('FORM.EDUCATION.LEVEL'),
                placeholder: t('FORM.EDUCATION.LEVEL_PLACEHOLDER')
            },
            {
                key: 'title',
                label: t('FORM.EDUCATION.DEGREE'),
                placeholder: t('FORM.EDUCATION.DEGREE_PLACEHOLDER')
            }
        ]}
      />

      <DocumentUpload
        t={t}
        initialDocuments={documentsList}
        onDocumentSelect={onDocumentSelect}
        activeDocId={activeDocId}
        onChange={setDocumentsList}
        authAxios={authAxios}
      />

      <DynamicListSection
        title={t('FORM.LINKS.TITLE')}
        items={linksList}
        addButtonLabel={t('FORM.LINKS.ADD_BUTTON')}

        onAdd={() => addItem({ type: '', url: '' }, linksList, setLinksList)}
        onRemove={(index) => removeItem(index, linksList, setLinksList)}
        onUpdate={(index, field, value) => updateList(index, field, value, linksList, setLinksList)}

        fields={[
            {
                key: 'type',
                label: t('FORM.LINKS.TYPE'),
                placeholder: t('FORM.LINKS.TYPE_PLACEHOLDER')
            },
            { key: 'url',  label: 'URL', placeholder: 'https://...' }
        ]}
      />

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-darkGray mb-4">
            {t('FORM.ADDITIONAL.TITLE')}
        </h2>
        <TextAreaField
          label={t('FORM.ADDITIONAL.COMMENTS')}
          placeholder={t('FORM.ADDITIONAL.COMMENTS_PLACEHOLDER')}
          rows={5}
          {...register("comments")}
        />
      </section>

      <div className="flex justify-center gap-4 pt-2 mt-6">
        <FormButton
          type="button"
            onClick={() => {
              if (typeof onCancel === "function") {
                onCancel();
                return;
              }
              navigate("/candidates");
            }}
          className="border border-secondary bg-white text-darkGray hover:bg-lightGray"
        >
          {t('FORM.ACTIONS.CANCEL')}
        </FormButton>

        <FormButton
          type="submit"
          className="bg-primary text-white hover:opacity-90"
          disabled={!isValid}
        >
          {t('FORM.ACTIONS.SAVE')}
        </FormButton>
      </div>

    </form>
  );
};

export default Form;